// src/pages/finance/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
  Grow,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip as MuiTooltip,
  Chip,
  Snackbar,
  InputAdornment
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { es } from 'date-fns/locale';
import {
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ChartIcon,
  WhatsApp as WhatsAppIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Download as ExportIcon,
  CardGiftcard as GiftIcon
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Importar el componente mejorado de distribución de pagos
import PaymentDistributionPanel from '../../components/finance/PaymentDistributionPanel';

// Definición de colores personalizados premium
const colors = {
  primary: '#6366f1',    // Violet
  secondary: '#06b6d4',  // Cyan
  tertiary: '#a855f7',   // Purple
  success: '#10b981',    // Emerald
  warning: '#f59e0b',    // Amber
  danger: '#ef4444',     // Red
  info: '#3b82f6',       // Blue
  grey: '#64748b',       // Slate
  glass: 'rgba(255, 255, 255, 0.75)',
  charts: {
    income: '#6366f1',
    expected: '#10b981',
    pending: '#f59e0b',
    revenue: '#3b82f6',
    pie: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981']
  }
};

const DEFAULT_VISUAL_TEMPLATE = '🔔 *RECORDATORIO DE PAGO - YINOWISP* 🌐\n\nEstimado(a) *{client_name}*, recibas un cordial saludo de parte de *YinoWISP*. ✨\n\nTe recordamos amablemente que cuentas con un recibo pendiente de pago:\n\n📄 *Detalle del Comprobante:*\n • *N° Comprobante:* #{invoice_number}\n 💰 *Monto Adeudado:* S/ {amount}\n 📅 *Fecha de Vencimiento:* {due_date}\n\n💡 *Evita la suspensión de tu servicio:*\nPuedes realizar tu pago y reportar tu comprobante de forma rápida y segura desde tu portal:\n🔗 {link_portal}\n\n¡Agradecemos tu preferencia y puntualidad! 🙌';

const SPANISH_MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Componente de tarjeta de estadísticas mejorado
const StatCard = ({ title, value, icon: Icon, color, subtitle, delay = 0 }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay * 100}ms` }}>
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px',
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
        borderLeft: `5px solid ${color}`,
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.1)',
        }
      }}
    >
      <CardContent className="p-5">
        <Box className="flex items-center justify-between">
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.grey, fontWeight: 600, letterSpacing: '0.5px' }}>
              {title}
            </Typography>
            <Typography variant="h4" className="mt-2 font-bold text-slate-800">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: colors.grey, display: 'block', marginTop: '6px', fontWeight: 500 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            className="w-12 h-12 rounded-2xl flex items-center justify-center"
            sx={{
              backgroundColor: `${color}15`,
              boxShadow: `0 0 0 4px ${color}05`,
            }}
          >
            <Icon sx={{ color: color, fontSize: 26 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Zoom>
);

// Tooltip personalizado para gráficos
const CustomTooltip = ({ active, payload, label, valuePrefix = 'S/ ', valueSuffix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Typography sx={{ color: '#1e293b', fontWeight: 700, marginBottom: '6px', fontSize: '0.9rem' }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={`item-${index}`} className="flex items-center mt-1.5">
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: entry.color,
                marginRight: '8px',
              }}
            />
            <Typography sx={{ color: '#475569', fontSize: '0.825rem', fontWeight: 500 }}>
              {entry.name}: <span style={{ fontWeight: 700, color: '#0f172a' }}>{valuePrefix}{parseFloat(entry.value).toLocaleString()}{valueSuffix}</span>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const FinanceDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const cellSx = {
    px: { xs: 1.2, sm: 2 },
    py: { xs: 1, sm: 1.5 },
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
    whiteSpace: 'nowrap'
  };
  const [stats, setStats] = useState({
    monthlyIncome: 0,
    pendingPayments: 0,
    completedPayments: 0,
    overdueInvoices: 0,
    pendingAmount: 0,
    overdueAmount: 0
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [revenueByZone, setRevenueByZone] = useState([]);
  const [financialSummary, setFinancialSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Proyecciones y Morosidad
  const [projections, setProjections] = useState([]);
  const [projectionMonths, setProjectionMonths] = useState(3); // 3 meses por defecto
  const [delinquencyData, setDelinquencyData] = useState({
    ranges: {
      '1-30 días': { count: 0, amount: 0 },
      '31-60 días': { count: 0, amount: 0 },
      '61-90 días': { count: 0, amount: 0 },
      'Más de 90 días': { count: 0, amount: 0 }
    },
    totalClients: 0,
    totalDebt: 0,
    clients: []
  });
  const [delinquencySearch, setDelinquencySearch] = useState('');

  // Configuración de plantillas
  const [financeSettings, setFinanceSettings] = useState({
    whatsapp_template: '',
    rejection_reasons: []
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [tempWhatsappTemplate, setTempWhatsappTemplate] = useState('');

  // Modales de prórroga (Promesa de Pago)
  const [promiseOpen, setPromiseOpen] = useState(false);
  const [promiseClient, setPromiseClient] = useState(null);
  const [promiseDate, setPromiseDate] = useState(null);
  const [promiseError, setPromiseError] = useState('');
  const [promiseLoading, setPromiseLoading] = useState(false);

  // Snackbar de notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const samplePaymentMethods = [
    { id: "1", name: "Transferencia BCP", count: 0, value: 0, percentage: 0 },
    { id: "2", name: "Yape", count: 0, value: 0, percentage: 0 },
    { id: "3", name: "Plin", count: 0, value: 0, percentage: 0 },
    { id: "4", name: "Efectivo", count: 0, value: 0, percentage: 0 }
  ];

  useEffect(() => {
    fetchDashboardData();
    fetchProjections();
    fetchDelinquency();
    fetchFinanceSettings();
  }, [selectedYear]);

  useEffect(() => {
    fetchProjections();
  }, [projectionMonths]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/finance/dashboard?year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Error al cargar datos del dashboard');
      const data = await response.json();

      setStats({
        monthlyIncome: parseFloat(data.stats?.monthly_income || data.stats?.monthlyIncome || 0),
        pendingPayments: parseInt(data.stats?.pending_payments || data.stats?.pendingPayments || 0),
        completedPayments: parseInt(data.stats?.completed_payments || data.stats?.completedPayments || 0),
        overdueInvoices: parseInt(data.stats?.overdue_invoices || data.stats?.overdueInvoices || 0),
        pendingAmount: parseFloat(data.stats?.pending_amount || data.stats?.pendingAmount || 0),
        overdueAmount: parseFloat(data.stats?.overdue_amount || data.stats?.overdueAmount || 0),
        courtesyClientsCount: parseInt(data.stats?.courtesy_clients_count || data.stats?.courtesyClientsCount || 0),
        totalDonatedValueMonth: parseFloat(data.stats?.total_donated_value_month || data.stats?.totalDonatedValueMonth || 0)
      });

      const processedMonthlyData = (data.monthlyData || []).map(item => ({
        ...item,
        income: parseFloat(item.income) || 0,
        expected: parseFloat(item.expected) || 0
      }));

      setMonthlyData(processedMonthlyData);
      setPaymentMethods(data.paymentMethods || []);
      setRevenueByZone(data.revenueByZone || []);
      setFinancialSummary(data.financialSummaryTable || []);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjections = async () => {
    try {
      const response = await fetch(`/api/finance/projection?months=${projectionMonths}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjections(data);
      }
    } catch (err) {
      console.error('Error al cargar proyecciones:', err);
    }
  };

  const fetchDelinquency = async () => {
    try {
      const response = await fetch('/api/finance/delinquency', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDelinquencyData(data);
      }
    } catch (err) {
      console.error('Error al cargar morosidad:', err);
    }
  };

  const fetchFinanceSettings = async () => {
    try {
      const response = await fetch('/api/finance/settings', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFinanceSettings(data);
        setTempWhatsappTemplate(data.whatsapp_template || DEFAULT_VISUAL_TEMPLATE);
      }
    } catch (err) {
      console.error('Error al obtener configuraciones:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          whatsapp_template: tempWhatsappTemplate
        })
      });

      if (response.ok) {
        setFinanceSettings(prev => ({ ...prev, whatsapp_template: tempWhatsappTemplate }));
        setSnackbar({
          open: true,
          message: 'Plantilla de WhatsApp guardada con éxito',
          severity: 'success'
        });
        setSettingsOpen(false);
      } else {
        throw new Error('Error al guardar configuración');
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error'
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handlePromiseSubmit = async () => {
    if (!promiseClient) return;
    if (!promiseDate) {
      setPromiseError('Debe seleccionar una fecha para la prórroga');
      return;
    }
    setPromiseLoading(true);
    setPromiseError('');
    try {
      const year = promiseDate.getFullYear();
      const month = String(promiseDate.getMonth() + 1).padStart(2, '0');
      const day = String(promiseDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      const response = await fetch('/api/finance/promise', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          clientId: promiseClient.id,
          promiseDate: formattedDate
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: data.message || 'Promesa de pago registrada exitosamente',
          severity: 'success'
        });
        setPromiseOpen(false);
        // Recargar datos
        fetchDelinquency();
        fetchDashboardData();
      } else {
        throw new Error(data.message || 'Error al registrar la prórroga');
      }
    } catch (err) {
      setPromiseError(err.message);
    } finally {
      setPromiseLoading(false);
    }
  };

  const openPromiseModal = (client) => {
    setPromiseClient(client);
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 3);
    setPromiseDate(defaultDate);
    setPromiseError('');
    setPromiseOpen(true);
  };

  const sendWhatsAppNotification = async (client) => {
    const template = financeSettings.whatsapp_template || DEFAULT_VISUAL_TEMPLATE;
    
    // Formatear parámetros
    const dateFormatted = client.last_due_date ? new Date(client.last_due_date).toLocaleDateString('es-PE') : 'Pendiente';
    const amountStr = parseFloat(client.total_debt).toFixed(2);
    const invoiceLabel = client.invoice_number || (client.overdue_invoices > 1 ? `${client.overdue_invoices} facturas` : 'recibo pendiente');
    const portalLink = financeSettings.client_portal_url || 'https://cliente.yinowisp.com';

    const compiledText = template
      .replace(/{client_name}/g, client.client_name || 'Cliente')
      .replace(/{cliente_nombre}/g, client.client_name || 'Cliente')
      .replace(/{invoice_number}/g, invoiceLabel)
      .replace(/{numero_factura}/g, invoiceLabel)
      .replace(/{amount}/g, amountStr)
      .replace(/{monto}/g, amountStr)
      .replace(/{due_date}/g, dateFormatted)
      .replace(/{fecha_vencimiento}/g, dateFormatted)
      .replace(/{link_portal}/g, portalLink)
      .replace(/{portal_link}/g, portalLink);

    const rawPhone = client.phone || client.telefono || '';
    const phoneNumber = rawPhone ? String(rawPhone).trim().replace(/\D/g, '') : '';
    const formattedPhone = phoneNumber.length === 9 ? `51${phoneNumber}` : phoneNumber;

    if (!formattedPhone) {
      setSnackbar({
        open: true,
        message: 'El cliente no tiene un número de teléfono válido registrado.',
        severity: 'warning'
      });
      return;
    }

    setSnackbar({
      open: true,
      message: `Enviando cobranza por WhatsApp a ${client.client_name}...`,
      severity: 'info'
    });

    try {
      const response = await fetch('/api/whatsapp/send-custom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          clientId: client.id,
          number: formattedPhone,
          message: compiledText
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `¡Recordatorio enviado con éxito a ${client.client_name}!`,
          severity: 'success'
        });
      } else {
        // Fallback a wa.me si no está conectado en el servidor
        setSnackbar({
          open: true,
          message: `${data.message || 'WhatsApp desconectado en el servidor'}. Abriendo WhatsApp Web...`,
          severity: 'warning'
        });
        
        setTimeout(() => {
          const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(compiledText)}`;
          window.open(whatsappUrl, '_blank');
        }, 1500);
      }
    } catch (error) {
      console.error('Error enviando notificación de WhatsApp:', error);
      setSnackbar({
        open: true,
        message: 'Error de red. Abriendo WhatsApp Web como alternativa...',
        severity: 'warning'
      });
      setTimeout(() => {
        const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(compiledText)}`;
        window.open(whatsappUrl, '_blank');
      }, 1500);
    }
  };

  const formatCurrency = (value) => {
    return `S/ ${parseFloat(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <Box className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <CircularProgress size={60} sx={{ color: colors.primary }} />
        <Typography sx={{ color: colors.grey, marginTop: 2, fontWeight: 500 }}>
          Cargando consola financiera...
        </Typography>
      </Box>
    );
  }

  // Filtrado de morosos
  const filteredDelinquents = (delinquencyData.clients || []).filter(client => 
    client.client_name.toLowerCase().includes(delinquencySearch.toLowerCase()) ||
    (client.zone || '').toLowerCase().includes(delinquencySearch.toLowerCase())
  );

  // Datos para gráfico de pastel de morosidad
  const pieData = Object.entries(delinquencyData.ranges).map(([range, info], idx) => ({
    name: range,
    value: info.amount,
    count: info.count,
    color: colors.charts.pie[idx % colors.charts.pie.length]
  })).filter(item => item.value > 0);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Fade in={true} timeout={500}>
        <Box className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Consola Financiera
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey, mt: 0.5, fontWeight: 500 }}>
              Flujo de caja, conciliación bancaria y control de cobro de YinoWISP
            </Typography>
          </Box>
          <Box className="flex items-center gap-3">
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchDashboardData();
                fetchProjections();
                fetchDelinquency();
              }}
              variant="outlined"
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                borderColor: 'rgba(99, 102, 241, 0.2)',
                color: colors.primary,
                '&:hover': {
                  borderColor: colors.primary,
                  backgroundColor: 'rgba(99, 102, 241, 0.05)'
                }
              }}
            >
              Recargar
            </Button>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="year-select-label">Año</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={selectedYear}
                label="Año"
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.08)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.primary,
                  },
                }}
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Fade>

      {error && (
        <Fade in={true}>
          <Alert
            severity="error"
            className="mb-6"
            sx={{
              borderRadius: '16px',
              boxShadow: '0 8px 16px -1px rgba(0, 0, 0, 0.05)',
            }}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Tabs Principales */}
      <Tabs
        value={activeTab}
        onChange={(e, newTab) => setActiveTab(newTab)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 4,
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          '& .MuiTabs-indicator': {
            backgroundColor: colors.primary,
            height: '3px',
            borderRadius: '3px'
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            color: colors.grey,
            minHeight: '48px',
            whiteSpace: 'nowrap',
            '&.Mui-selected': {
              color: colors.primary
            }
          }
        }}
      >
        <Tab icon={<BarChartIcon sx={{ mr: 1, fontSize: 20 }} />} iconPosition="start" label="Resumen General" />
        <Tab icon={<TrendingUpIcon sx={{ mr: 1, fontSize: 20 }} />} iconPosition="start" label="Proyecciones de Flujo (Cashflow)" />
        <Tab icon={<WarningIcon sx={{ mr: 1, fontSize: 20 }} />} iconPosition="start" label="Cobranza y Morosidad" />
      </Tabs>

      {/* TAB 0: RESUMEN GENERAL */}
      {activeTab === 0 && (
        <Grow in={true}>
          <Box>
            <Grid container spacing={2.5} className="mb-6">
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  title="Ingresos del Mes"
                  value={formatCurrency(stats.monthlyIncome)}
                  icon={MoneyIcon}
                  color={colors.success}
                  subtitle="Registrados y aprobados"
                  delay={0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  title="Facturas por Cobrar"
                  value={stats.pendingPayments}
                  icon={PendingIcon}
                  color={colors.warning}
                  subtitle={formatCurrency(stats.pendingAmount)}
                  delay={1}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  title="Recibos Conciliados"
                  value={stats.completedPayments}
                  icon={CheckIcon}
                  color={colors.info}
                  subtitle="Comprobantes validados"
                  delay={2}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  title="Recibos Vencidos"
                  value={stats.overdueInvoices}
                  icon={WarningIcon}
                  color={colors.danger}
                  subtitle={formatCurrency(stats.overdueAmount)}
                  delay={3}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <StatCard
                  title="Valor Donado / Cortesía"
                  value={formatCurrency(stats.totalDonatedValueMonth || 0)}
                  icon={GiftIcon}
                  color="#8b5cf6"
                  subtitle={`${stats.courtesyClientsCount || 0} abonado(s) donado(s)`}
                  delay={4}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper
                  sx={{
                    p: { xs: 2, sm: 5 },
                    borderRadius: '24px',
                    background: colors.glass,
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.03)',
                    minWidth: 0,
                  }}
                >
                  <Box className="flex justify-between items-center mb-6">
                    <Box className="flex items-center">
                      <ChartIcon sx={{ color: '#475569', marginRight: 1.5, fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Ingresos Recaudados vs Esperados
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="h-96">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={monthlyData}>
                        <defs>
                          <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors.charts.income} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={colors.charts.income} stopOpacity={0.0} />
                          </linearGradient>
                          <linearGradient id="colorExpected" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={colors.charts.expected} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={colors.charts.expected} stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: colors.grey, fontWeight: 500, fontSize: '0.75rem' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: colors.grey, fontWeight: 500, fontSize: '0.75rem' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => `S/ ${value}`}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          height={36}
                          iconType="circle"
                          iconSize={10}
                          wrapperStyle={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: '#475569'
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="income"
                          name="Recaudado (Pagos Completados)"
                          stroke={colors.charts.income}
                          strokeWidth={3}
                          fill="url(#colorIncome)"
                          activeDot={{ r: 8, strokeWidth: 1, stroke: '#fff' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="expected"
                          name="Esperado (Facturado)"
                          stroke={colors.charts.expected}
                          strokeWidth={3}
                          fill="url(#colorExpected)"
                          strokeDasharray="4 4"
                          activeDot={{ r: 6, strokeWidth: 1, stroke: '#fff' }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <PaymentDistributionPanel
                  paymentMethods={paymentMethods.length > 0 ? paymentMethods : samplePaymentMethods}
                />
              </Grid>

              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: { xs: 2, sm: 5 },
                    borderRadius: '24px',
                    background: colors.glass,
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.03)',
                    minWidth: 0,
                  }}
                >
                  <Box className="flex justify-between items-center mb-6">
                    <Box className="flex items-center">
                      <BarChartIcon sx={{ color: '#475569', marginRight: 1.5, fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Desempeño de Recaudación por Zona
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenueByZone}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                          dataKey="name"
                          tick={{ fill: colors.grey, fontSize: '0.75rem', fontWeight: 500 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: colors.grey, fontSize: '0.75rem', fontWeight: 500 }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => `S/ ${value}`}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          height={36}
                          iconType="circle"
                          iconSize={10}
                          wrapperStyle={{
                            fontSize: '0.85rem',
                            fontWeight: 600
                          }}
                        />
                        <Bar
                          dataKey="revenue"
                          name="Recaudado (S/)"
                          fill={colors.charts.income}
                          radius={[6, 6, 0, 0]}
                          barSize={32}
                        />
                        <Bar
                          dataKey="pending"
                          name="Deuda Pendiente (S/)"
                          fill={colors.charts.pending}
                          radius={[6, 6, 0, 0]}
                          barSize={32}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>

              {/* Tabla de Resumen Financiero Mensual */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: { xs: 2, sm: 5 },
                    borderRadius: '24px',
                    background: colors.glass,
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.03)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'start', sm: 'center' }, gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarIcon sx={{ color: '#475569', marginRight: 1.5, fontSize: 24 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Resumen Detallado Mensual ({selectedYear})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="outlined"
                        startIcon={<ExportIcon />}
                        onClick={() => {
                          window.open(`/api/finance/reports/monthly-summary/excel?year=${selectedYear}&token=${localStorage.getItem('token')}`, '_blank');
                        }}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          borderColor: '#10b981',
                          color: '#10b981',
                          '&:hover': {
                            borderColor: '#059669',
                            backgroundColor: 'rgba(16, 185, 129, 0.05)'
                          }
                        }}
                      >
                        Excel
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ExportIcon />}
                        onClick={() => {
                          window.open(`/api/finance/reports/monthly-summary/pdf?year=${selectedYear}&token=${localStorage.getItem('token')}`, '_blank');
                        }}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          borderColor: '#ef4444',
                          color: '#ef4444',
                          '&:hover': {
                            borderColor: '#dc2626',
                            backgroundColor: 'rgba(239, 68, 68, 0.05)'
                          }
                        }}
                      >
                        PDF
                      </Button>
                    </Box>
                  </Box>

                  <TableContainer sx={{ borderRadius: '16px', border: '1px solid rgba(0, 0, 0, 0.05)', overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: { xs: 650, sm: '100%' } }}>
                      <TableHead sx={{ backgroundColor: 'rgba(99, 102, 241, 0.04)' }}>
                        <TableRow>
                          <TableCell sx={{ ...cellSx, fontWeight: 700, color: '#334155' }}>Mes</TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#334155' }}>Ingresos Internet</TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#334155' }}>Otros Ingresos</TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#334155' }}>Egresos</TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 700, color: '#334155' }}>Total Neto</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {financialSummary.map((row) => {
                          const isNegative = row.total < 0;
                          return (
                            <TableRow 
                              key={row.month_num}
                              sx={{ 
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                transition: 'background-color 0.2s ease'
                              }}
                            >
                              <TableCell sx={{ ...cellSx, fontWeight: 600, color: '#1e293b' }}>
                                {SPANISH_MONTHS[row.month_num - 1] || row.month_name.trim()}
                              </TableCell>
                              <TableCell align="right" sx={{ ...cellSx, color: '#0f172a' }}>
                                {formatCurrency(row.internet_income)}
                              </TableCell>
                              <TableCell align="right" sx={{ ...cellSx, color: '#0f172a' }}>
                                {formatCurrency(row.other_income)}
                              </TableCell>
                              <TableCell align="right" sx={{ ...cellSx, color: '#e11d48' }}>
                                {formatCurrency(row.expenses)}
                              </TableCell>
                              <TableCell 
                                align="right" 
                                sx={{ 
                                  ...cellSx,
                                  fontWeight: 700, 
                                  color: isNegative ? '#e11d48' : '#10b981' 
                                }}
                              >
                                {formatCurrency(row.total)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {/* Fila de Totales */}
                        <TableRow sx={{ backgroundColor: 'rgba(99, 102, 241, 0.06)', '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.06)' } }}>
                          <TableCell sx={{ ...cellSx, fontWeight: 800, color: '#1e293b' }}>TOTAL ANUAL</TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 800, color: '#1e293b' }}>
                            {formatCurrency(financialSummary.reduce((sum, r) => sum + parseFloat(r.internet_income || 0), 0))}
                          </TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 800, color: '#1e293b' }}>
                            {formatCurrency(financialSummary.reduce((sum, r) => sum + parseFloat(r.other_income || 0), 0))}
                          </TableCell>
                          <TableCell align="right" sx={{ ...cellSx, fontWeight: 800, color: '#e11d48' }}>
                            {formatCurrency(financialSummary.reduce((sum, r) => sum + parseFloat(r.expenses || 0), 0))}
                          </TableCell>
                          <TableCell 
                            align="right" 
                            sx={{ 
                              ...cellSx,
                              fontWeight: 800, 
                              color: financialSummary.reduce((sum, r) => sum + parseFloat(r.total || 0), 0) < 0 ? '#e11d48' : '#10b981'
                            }}
                          >
                            {formatCurrency(financialSummary.reduce((sum, r) => sum + parseFloat(r.total || 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Grow>
      )}

      {/* TAB 1: PROYECCIONES DE FLUJO (CASHFLOW) */}
      {activeTab === 1 && (
        <Grow in={true}>
          <Box>
            <Grid container spacing={3} className="mb-6">
              <Grid item xs={12} md={4}>
                <StatCard
                  title="Cobro Recurrente Estimado"
                  value={formatCurrency(projections.reduce((acc, curr) => acc + parseFloat(curr.expected_amount || 0), 0) / (projections.length || 1))}
                  icon={TrendingUpIcon}
                  color={colors.primary}
                  subtitle="Promedio mensual (planes activos)"
                  delay={0}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard
                  title="Facturas Pendientes Próximas"
                  value={formatCurrency(projections.reduce((acc, curr) => acc + parseFloat(curr.pending_amount || 0), 0))}
                  icon={PendingIcon}
                  color={colors.warning}
                  subtitle={`Total en el periodo de ${projectionMonths} meses`}
                  delay={1}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <StatCard
                  title="Flujo Total Esperado"
                  value={formatCurrency(projections.reduce((acc, curr) => acc + parseFloat(curr.total_expected || 0), 0))}
                  icon={MoneyIcon}
                  color={colors.success}
                  subtitle="Suma proyectada de ingresos"
                  delay={2}
                />
              </Grid>
            </Grid>

            <Paper
              sx={{
                p: { xs: 2, sm: 5 },
                borderRadius: '24px',
                background: colors.glass,
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.03)',
                minWidth: 0,
              }}
            >
              <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    Proyección de Flujo de Caja (Cashflow Pro)
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.grey, fontWeight: 500 }}>
                    Combina la facturación recurrente teórica de planes activos y las facturas ya emitidas pendientes de pago
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="projection-months-label">Periodo</InputLabel>
                  <Select
                    labelId="projection-months-label"
                    value={projectionMonths}
                    label="Periodo"
                    onChange={(e) => setProjectionMonths(e.target.value)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value={3}>Próximos 3 meses</MenuItem>
                    <MenuItem value={6}>Próximos 6 meses</MenuItem>
                    <MenuItem value={9}>Próximos 9 meses</MenuItem>
                    <MenuItem value={12}>Próximos 12 meses</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={projections}>
                    <defs>
                      <linearGradient id="colorTotalExpected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.success} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={colors.success} stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="colorRecurrentExpected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={colors.primary} stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: colors.grey, fontWeight: 500, fontSize: '0.75rem' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: colors.grey, fontWeight: 500, fontSize: '0.75rem' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `S/ ${value}`}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      height={36}
                      iconType="circle"
                      iconSize={10}
                      wrapperStyle={{
                        fontSize: '0.85rem',
                        fontWeight: 600
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="total_expected"
                      name="Esperado Total (Plan + Pendiente)"
                      stroke={colors.success}
                      strokeWidth={3}
                      fill="url(#colorTotalExpected)"
                      activeDot={{ r: 8, strokeWidth: 1, stroke: '#fff' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="expected_amount"
                      name="Solo Abono Recurrente de Planes"
                      stroke={colors.primary}
                      strokeWidth={2.5}
                      fill="url(#colorRecurrentExpected)"
                      strokeDasharray="4 4"
                      activeDot={{ r: 6, strokeWidth: 1, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Box>
        </Grow>
      )}

      {/* TAB 2: COBRANZA Y MOROSIDAD */}
      {activeTab === 2 && (
        <Grow in={true}>
          <Box>
            <Grid container spacing={3} className="mb-6">
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Clientes Morosos"
                  value={delinquencyData.totalClients}
                  icon={PeopleIcon}
                  color={colors.danger}
                  subtitle="Con facturas vencidas"
                  delay={0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Deuda Vencida Total"
                  value={formatCurrency(delinquencyData.totalDebt)}
                  icon={MoneyIcon}
                  color={colors.danger}
                  subtitle="Fuga de caja actual"
                  delay={1}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Rango Crítico (1-30d)"
                  value={delinquencyData.ranges['1-30 días'].count}
                  icon={WarningIcon}
                  color={colors.warning}
                  subtitle={formatCurrency(delinquencyData.ranges['1-30 días'].amount)}
                  delay={2}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Rango Crítico (+90d)"
                  value={delinquencyData.ranges['Más de 90 días'].count}
                  icon={WarningIcon}
                  color={colors.danger}
                  subtitle={formatCurrency(delinquencyData.ranges['Más de 90 días'].amount)}
                  delay={3}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3} className="mb-6">
              {/* Gráfico de pastel de morosidad */}
              {pieData.length > 0 && (
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: { xs: 2, sm: 4 },
                      borderRadius: '24px',
                      height: '100%',
                      background: colors.glass,
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.4)',
                      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.03)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      minWidth: 0,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b', textAlign: 'center' }}>
                      Distribución de Deuda por Antigüedad
                    </Typography>
                    <Box className="h-64 flex justify-center items-center">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box className="flex flex-col gap-2 mt-4">
                      {pieData.map((item, idx) => (
                        <Box key={idx} className="flex justify-between items-center px-4 py-1.5 rounded-xl" sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                          <Box className="flex items-center gap-2">
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                              {item.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                            {formatCurrency(item.value)} ({item.count})
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              )}

              {/* Tabla de Clientes Morosos */}
              <Grid item xs={12} md={pieData.length > 0 ? 8 : 12}>
                <Paper
                  sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: '24px',
                    background: colors.glass,
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.03)',
                    minWidth: 0,
                  }}
                >
                  <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        Cobranza Activa (Clientes con Retraso)
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey, fontWeight: 500 }}>
                        Listado de clientes suspendidos o prontos a suspender con deuda pendiente
                      </Typography>
                    </Box>
                    <Box className="flex gap-2 w-full sm:w-auto">
                      <TextField
                        size="small"
                        placeholder="Buscar por cliente o zona..."
                        value={delinquencySearch}
                        onChange={(e) => setDelinquencySearch(e.target.value)}
                        sx={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0,0,0,0.08)'
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: colors.grey }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <MuiTooltip title="Editar Plantilla de Notificación">
                        <IconButton
                          onClick={() => setSettingsOpen(true)}
                          sx={{
                            backgroundColor: 'white',
                            border: '1px solid rgba(0,0,0,0.08)',
                            borderRadius: '12px',
                            color: colors.primary,
                            '&:hover': {
                              backgroundColor: 'rgba(99, 102, 241, 0.05)'
                            }
                          }}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </MuiTooltip>
                    </Box>
                  </Box>

                  <TableContainer component={Box} sx={{ 
                    maxHeight: 400, 
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                      height: '6px',
                      width: '6px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                      borderRadius: '4px'
                    }
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Cliente</TableCell>
                          <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Zona</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Recibos Vencidos</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Monto Adeudado</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Días Retraso</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#f8fafc' }}>Acciones de Cobro</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredDelinquents.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ py: 6, color: colors.grey }}>
                              No se encontraron clientes morosos que coincidan con la búsqueda.
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredDelinquents.map((client) => {
                            let rangeColor = colors.success;
                            if (client.days_overdue > 60) rangeColor = colors.danger;
                            else if (client.days_overdue > 30) rangeColor = colors.warning;
                            else rangeColor = colors.info;

                            return (
                              <TableRow key={client.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
                                  {client.client_name}
                                </TableCell>
                                <TableCell sx={{ color: '#475569' }}>{client.zone || 'General'}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={`${client.overdue_invoices} Recibo(s)`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                      color: colors.danger,
                                      fontWeight: 700,
                                      borderRadius: '8px'
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                  {formatCurrency(client.total_debt)}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={`${client.days_overdue} días`}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${rangeColor}15`,
                                      color: rangeColor,
                                      fontWeight: 700,
                                      borderRadius: '8px'
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Box className="flex items-center justify-center gap-1.5">
                                    <MuiTooltip title="Recordatorio por WhatsApp">
                                      <IconButton
                                        size="small"
                                        onClick={() => sendWhatsAppNotification(client)}
                                        sx={{
                                          color: '#25D366',
                                          backgroundColor: '#25D36615',
                                          '&:hover': { backgroundColor: '#25D36625' }
                                        }}
                                      >
                                        <WhatsAppIcon fontSize="small" />
                                      </IconButton>
                                    </MuiTooltip>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<CalendarIcon />}
                                      onClick={() => openPromiseModal(client)}
                                      sx={{
                                        borderRadius: '8px',
                                        fontSize: '0.75rem',
                                        py: 0.5,
                                        px: 1.5,
                                        textTransform: 'none',
                                        borderColor: 'rgba(99, 102, 241, 0.3)',
                                        color: colors.primary,
                                        '&:hover': {
                                          backgroundColor: 'rgba(99, 102, 241, 0.05)',
                                          borderColor: colors.primary
                                        }
                                      }}
                                    >
                                      Prórroga
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Grow>
      )}

      {/* DIÁLOGO: EDITAR PLANTILLA DE WHATSAPP */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>
          Configuración de Mensaje de WhatsApp
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.grey, mb: 3 }}>
            Personalice el recordatorio de cobro enviado a los clientes. Puede usar las siguientes variables dinámicas en el texto:
          </Typography>
          <Box className="grid grid-cols-3 gap-2 mb-4">
            <Paper variant="outlined" className="p-2 text-center" sx={{ borderRadius: '8px', backgroundColor: '#f8fafc' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.primary }}>{"{client_name}"}</Typography>
              <Typography variant="caption" display="block">Nombre Cliente</Typography>
            </Paper>
            <Paper variant="outlined" className="p-2 text-center" sx={{ borderRadius: '8px', backgroundColor: '#f8fafc' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.primary }}>{"{invoice_number}"}</Typography>
              <Typography variant="caption" display="block">Recibo / Factura</Typography>
            </Paper>
            <Paper variant="outlined" className="p-2 text-center" sx={{ borderRadius: '8px', backgroundColor: '#f8fafc' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.primary }}>{"{amount}"}</Typography>
              <Typography variant="caption" display="block">Monto Deuda</Typography>
            </Paper>
            <Paper variant="outlined" className="p-2 text-center" sx={{ borderRadius: '8px', backgroundColor: '#f8fafc' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.primary }}>{"{due_date}"}</Typography>
              <Typography variant="caption" display="block">Vencimiento</Typography>
            </Paper>
            <Paper variant="outlined" className="p-2 text-center" sx={{ borderRadius: '8px', backgroundColor: '#f8fafc' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.primary }}>{"{link_portal}"}</Typography>
              <Typography variant="caption" display="block">Link del Portal</Typography>
            </Paper>
          </Box>
          <Box className="flex justify-between items-center mb-2">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
              Plantilla del Mensaje:
            </Typography>
            <Button
              size="small"
              onClick={() => setTempWhatsappTemplate(DEFAULT_VISUAL_TEMPLATE)}
              sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, color: colors.primary }}
            >
              ✨ Cargar Plantilla Visual por Defecto
            </Button>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={10}
            variant="outlined"
            value={tempWhatsappTemplate}
            onChange={(e) => setTempWhatsappTemplate(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'monospace',
                fontSize: '13px'
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSettingsOpen(false)}
            sx={{ borderRadius: '10px', textTransform: 'none', color: colors.grey }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={settingsLoading}
            variant="contained"
            startIcon={settingsLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              backgroundColor: colors.primary,
              '&:hover': { backgroundColor: '#4f46e5' }
            }}
          >
            Guardar Plantilla
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIÁLOGO: REGISTRAR PRÓRROGA (PROMESA DE PAGO) */}
      <Dialog
        open={promiseOpen}
        onClose={() => setPromiseOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>
          Registrar Prórroga / Promesa
        </DialogTitle>
        <DialogContent>
          {promiseError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              {promiseError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ color: colors.grey, mb: 3 }}>
            Otorgue una extensión temporal al cliente <strong>{promiseClient?.client_name}</strong>. Esto reactivará el servicio en MikroTik de inmediato hasta que se cumpla el plazo.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DatePicker
              label="Fecha Promesa de Pago"
              value={promiseDate}
              onChange={setPromiseDate}
              format="dd/MM/yyyy"
              slotProps={{ textField: { fullWidth: true, size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } } } }}
              minDate={new Date()}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPromiseOpen(false)}
            sx={{ borderRadius: '10px', textTransform: 'none', color: colors.grey }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handlePromiseSubmit}
            disabled={promiseLoading}
            variant="contained"
            startIcon={promiseLoading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              backgroundColor: colors.primary,
              '&:hover': { backgroundColor: '#4f46e5' }
            }}
          >
            Reactivar Cliente
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR DE AVISOS */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: '12px', boxShadow: '0 8px 16px rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinanceDashboard;